/**
 * Posúvanie riadkov v zostave (KOLO 176) — nové poradie pre
 * `POST /zostavy/{id}/poradie-dielcov`.
 *
 * Server chce **celé** poradie koreňových riadkov bez paušálu (overí tak,
 * že tabuľka nie je zastaraná). Ťahať sa dajú len riadky kusovníka;
 * riadok, ktorý sa neťahá (operácia na zostave), ostane na svojom mieste
 * a ťahané sa prelejú okolo neho.
 */

import { jeOperaciaRiadok, jePausalZostavy } from "@/lib/operacie";
import type { Dielec } from "@/api/schemas";

/**
 * Koreňové riadky zostavy bez paušálu (čo chce server, v jeho poradí)
 * a tie z nich, ktoré sa dajú ťahať. Vrstvy a vnorené operácie idú so
 * svojím rodičom.
 */
export function poradieZostavy(dielce: readonly Dielec[]): {
  korene: string[];
  posuvatelne: Set<string>;
} {
  const hore = dielce.filter((d) => !d.parent_dielec_id);
  return {
    korene: hore.filter((d) => !jePausalZostavy(d)).map((d) => d.id),
    posuvatelne: new Set(hore.filter((d) => !jeOperaciaRiadok(d)).map((d) => d.id)),
  };
}

/** `id` pred alebo za `cielId`; poradie ostatných sa nemení. */
export function posunRiadok(
  poradie: readonly string[],
  posuvatelne: ReadonlySet<string>,
  id: string,
  cielId: string,
  za: boolean,
): string[] {
  if (id === cielId || !posuvatelne.has(id) || !posuvatelne.has(cielId)) {
    return [...poradie];
  }
  const bez = poradie.filter((x) => posuvatelne.has(x) && x !== id);
  const i = bez.indexOf(cielId);
  bez.splice(za ? i + 1 : i, 0, id);
  return dosad(poradie, posuvatelne, bez);
}

/** Alt+↑ / Alt+↓ — o jeden posúvateľný riadok vyššie alebo nižšie. */
export function posunOJeden(
  poradie: readonly string[],
  posuvatelne: ReadonlySet<string>,
  id: string,
  smer: -1 | 1,
): string[] {
  const pohyblive = poradie.filter((x) => posuvatelne.has(x));
  const i = pohyblive.indexOf(id);
  const j = i + smer;
  if (i < 0 || j < 0 || j >= pohyblive.length) return [...poradie];
  const vymena = [...pohyblive];
  vymena[i] = pohyblive[j] as string;
  vymena[j] = pohyblive[i] as string;
  return dosad(poradie, posuvatelne, vymena);
}

/** Ťahané riadky do ich miest, ostatné ostanú, kde boli. */
function dosad(
  poradie: readonly string[],
  posuvatelne: ReadonlySet<string>,
  nove: readonly string[],
): string[] {
  let k = 0;
  return poradie.map((x) => (posuvatelne.has(x) ? (nove[k++] ?? x) : x));
}

export function rovnakePoradie(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}
